import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../../../../services/firebase';
import { COLLECTIONS } from '../../../../../../services/collections';
import MembershipService from '../../../../../../services/membership_service';
import MemberMembershipService from '../../../../../../services/member_membership_service';
import ConfirmDialog from '../../../../../../components/Common/ConfirmDialog';
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from '../../../../../../services/audit_service';
import { NeoButton, NeoInput } from '../../../../../../components/Common';

import { formatCurrency } from '../../../../../../utils/format';
import { toISODate, formatDate } from '../../../../../../utils/date';

/**
 * ActionChangePlan
 * Permite trocar o plano atual de um membro por outro do catálogo.
 * 
 * Lógica de conversão:
 * 1. Calcula o saldo restante em R$ do plano atual (valor diário × dias restantes)
 * 2. Calcula o valor diário do novo plano
 * 3. Converte o saldo em dias do novo plano
 * 4. Cancela o plano atual e cria o novo com a data calculada
 */
const ActionChangePlan = ({ membership, idTenant, user, onRefresh }) => {
    const [catalogPlans, setCatalogPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [selectedPlanId, setSelectedPlanId] = useState("");
    const [reason, setReason] = useState("");
    const [loading, setLoading] = useState(false);

    // Buscar planos do catálogo
    useEffect(() => {
        if (!idTenant) return;
        setLoadingPlans(true);
        MembershipService.getMemberships(idTenant)
            .then(plans => {
                // Filtrar apenas planos ativos e diferentes do atual
                const filtered = plans.filter(p =>
                    p.status === "active" && p.id !== membership.idMembershipTarget
                );
                setCatalogPlans(filtered);
            })
            .catch(err => {
                console.error("Erro ao buscar planos do catálogo:", err);
                toast.error("Erro ao carregar planos disponíveis.");
            })
            .finally(() => setLoadingPlans(false));
    }, [idTenant, membership.idMembershipTarget]);

    // Cálculos do plano atual
    const currentPlanCalc = useMemo(() => {
        const today = new Date();
        const startDate = new Date(membership.startDate + "T00:00:00");
        const endDate = new Date(membership.endDate + "T23:59:59");

        const totalDays = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
        const daysRemaining = Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)));
        const dailyValue = (membership.price || 0) / totalDays;
        const remainingValue = dailyValue * daysRemaining;

        return { totalDays, daysRemaining, dailyValue, remainingValue };
    }, [membership]);

    // Cálculos do novo plano selecionado
    const newPlanCalc = useMemo(() => {
        if (!selectedPlanId) return null;

        const plan = catalogPlans.find(p => p.id === selectedPlanId);
        if (!plan) return null;

        // Calcular total de dias do novo plano
        let newTotalDays = 0;
        switch (plan.durationType) {
            case "monthly":
                newTotalDays = (plan.duration || 1) * 30;
                break;
            case "yearly":
                newTotalDays = (plan.duration || 1) * 365;
                break;
            case "days":
                newTotalDays = plan.duration || 30;
                break;
            default:
                newTotalDays = 30;
        }

        const newPlanValue = plan.value || 0;
        const newDailyValue = newTotalDays > 0 ? newPlanValue / newTotalDays : 0;

        // Converter saldo restante em dias do novo plano
        const convertedDays = newDailyValue > 0
            ? Math.floor(currentPlanCalc.remainingValue / newDailyValue)
            : 0;

        // Diferença de valor (positivo = aluno recebe mais dias, negativo = menos dias)
        const valueDifference = currentPlanCalc.remainingValue - (newDailyValue * convertedDays);

        // Data de início = hoje, data fim = hoje + dias convertidos
        const today = new Date();
        const newEndDate = new Date(today);
        newEndDate.setDate(newEndDate.getDate() + convertedDays);

        return {
            plan,
            newTotalDays,
            newPlanValue,
            newDailyValue,
            convertedDays,
            valueDifference,
            newStartDate: toISODate(today),
            newEndDate: toISODate(newEndDate),
        };
    }, [selectedPlanId, catalogPlans, currentPlanCalc]);

    const handleConfirm = async () => {
        if (!newPlanCalc || !newPlanCalc.plan) return;

        if (newPlanCalc.convertedDays <= 0) {
            return toast.warning("O saldo restante não é suficiente para converter em dias no novo plano.");
        }

        const confirmed = await ConfirmDialog({
            title: "Confirmar Troca de Plano?",
            text: `O plano "${membership.membershipName}" será cancelado e substituído pelo plano "${newPlanCalc.plan.name}" com ${newPlanCalc.convertedDays} dia(s) de vigência (até ${formatDate(newPlanCalc.newEndDate)}). Deseja confirmar?`,
            confirmButtonText: "Sim, trocar plano!",
        });

        if (!confirmed) return;

        setLoading(true);
        try {
            // 1. Cancelar o plano atual
            await MemberMembershipService.cancel(
                membership.id,
                idTenant,
                user,
                membership,
                `Troca de plano para "${newPlanCalc.plan.name}" — ${reason || "Solicitação do aluno"}`,
            );

            // 2. Criar novo memberMembership
            const newMemberMembership = {
                idMember: membership.idMember,
                memberName: membership.memberName || "",
                memberEmail: membership.memberEmail || "",
                memberPhone: membership.memberPhone || "",
                memberPhotoUrl: membership.memberPhotoUrl || "",
                membershipName: newPlanCalc.plan.name,
                idMembershipTarget: newPlanCalc.plan.id,
                startDate: newPlanCalc.newStartDate,
                endDate: newPlanCalc.newEndDate,
                status: "active",
                price: 0, // Não gera nova receita (é conversão do saldo)
                originalValue: newPlanCalc.newPlanValue,
                durationType: newPlanCalc.plan.durationType,
                duration: newPlanCalc.plan.duration,
                maxSuspensionDays: newPlanCalc.plan.maxSuspensionDays || 0,
                disponibleSuspensionDays: newPlanCalc.plan.maxSuspensionDays || 0,
                allowsSuspension: newPlanCalc.plan.allowsSuspension || false,
                requiresClasses: newPlanCalc.plan.durationType === "classes",
                classesPerWeek: newPlanCalc.plan.maxSessionsPerWeek || null,
                changedFrom: membership.id,
                changeReason: reason || "Troca de plano",
                createdAt: serverTimestamp(),
                createdBy: {
                    uid: user?.uid || "system",
                    name: user?.displayName || user?.fullName || user?.email || "Sistema"
                },
            };

            const colRef = collection(db, "tenants", idTenant, COLLECTIONS.MEMBER_MEMBERSHIPS);
            await addDoc(colRef, newMemberMembership);

            // 3. Auditoria
            AuditService.log({
                idTenant,
                user,
                module: AUDIT_MODULES.MEMBERSHIPS,
                action: AUDIT_ACTIONS.UPDATE,
                entityId: membership.id,
                entityName: `Troca: ${membership.membershipName} → ${newPlanCalc.plan.name}`,
                description: `Trocou o plano "${membership.membershipName}" para "${newPlanCalc.plan.name}" do aluno "${membership.memberName}". Saldo convertido: ${formatCurrency(currentPlanCalc.remainingValue)} → ${newPlanCalc.convertedDays} dias. Nova vigência: ${formatDate(newPlanCalc.newStartDate)} a ${formatDate(newPlanCalc.newEndDate)}.`,
                details: {
                    planoAnterior: membership.membershipName,
                    planoNovo: newPlanCalc.plan.name,
                    saldoRestante: currentPlanCalc.remainingValue,
                    diasConvertidos: newPlanCalc.convertedDays,
                    novaDataFim: newPlanCalc.newEndDate,
                    motivo: reason || "Troca de plano"
                }
            });

            toast.success("Plano trocado com sucesso!");
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error("Erro ao trocar plano:", error);
            toast.error("Erro ao trocar o plano. Tente novamente.");
        } finally {
            setLoading(false);
        }
    };

    if (loadingPlans) {
        return (
            <div className="p-4 border mt-3 bg-white text-center" style={{ borderRadius: '12px' }}>
                <span className="text-muted font-size-13"><i className="mdi mdi-loading mdi-spin me-2 fs-5"></i>Carregando planos disponíveis...</span>
            </div>
        );
    }

    return (
        <div className="p-4 border mt-3 bg-white shadow-sm" style={{ borderRadius: '12px', borderColor: 'rgba(0, 0, 0, 0.06)', fontFamily: 'Outfit, sans-serif' }}>
            <div className="d-flex align-items-center mb-3">
                <i className="mdi mdi-swap-horizontal-bold fs-4 me-2 text-muted" style={{ color: 'var(--color-brand-secondary) !important' }}></i>
                <h6 className="fw-bold mb-0 text-uppercase" style={{ letterSpacing: "0.5px", color: 'var(--color-brand-primary)', fontFamily: 'Outfit, sans-serif' }}>
                    Trocar Plano
                </h6>
            </div>

            <p className="text-muted font-size-13 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                O saldo restante do plano atual será convertido em dias no novo plano selecionado, com base no valor diário proporcional de cada contrato.
            </p>

            {/* Resumo do plano atual */}
            <div className="p-3 bg-white mb-4 border shadow-sm" style={{ borderRadius: '12px', borderColor: 'rgba(72, 39, 18, 0.08)' }}>
                <div className="font-size-11 text-muted text-uppercase fw-bold mb-2" style={{ letterSpacing: "0.5px" }}>
                    Plano Atual
                </div>
                <Row className="g-2">
                    <Col xs={6} md={3}>
                        <div className="font-size-11 text-muted">Plano</div>
                        <div className="font-size-13 fw-bold text-dark">{membership.membershipName}</div>
                    </Col>
                    <Col xs={6} md={3}>
                        <div className="font-size-11 text-muted">Valor Total</div>
                        <div className="font-size-13 fw-bold text-dark">{formatCurrency(membership.price)}</div>
                    </Col>
                    <Col xs={6} md={3}>
                        <div className="font-size-11 text-muted">Dias Restantes</div>
                        <div className="font-size-13 fw-bold text-dark">{currentPlanCalc.daysRemaining} dias</div>
                    </Col>
                    <Col xs={6} md={3}>
                        <div className="font-size-11 text-muted">Saldo em R$</div>
                        <div className="font-size-13 fw-bold" style={{ color: 'var(--color-brand-accent)' }}>{formatCurrency(currentPlanCalc.remainingValue)}</div>
                    </Col>
                </Row>
            </div>

            {/* Seleção do novo plano */}
            <Row className="g-3 mb-3">
                <Col md={6}>
                    <NeoInput
                        label="Novo Plano"
                        type="select"
                        value={selectedPlanId}
                        onChange={e => setSelectedPlanId(e.target.value)}
                        size="sm"
                    >
                        <option value="">Selecione o novo plano...</option>
                        {catalogPlans.map(plan => (
                            <option key={plan.id} value={plan.id}>
                                {plan.name} — {formatCurrency(plan.value)} ({plan.durationType === "monthly" ? `${plan.duration} mês(es)` : plan.durationType === "yearly" ? `${plan.duration} ano(s)` : `${plan.duration} dias`})
                            </option>
                        ))}
                    </NeoInput>
                </Col>
                <Col md={6}>
                    <NeoInput
                        label="Motivo (Para auditoria)"
                        placeholder="Ex: Aluno solicitou mais dias de treino..."
                        value={reason}
                        onChange={e => setReason(e.target.value)}
                        size="sm"
                    />
                </Col>
            </Row>

            {/* Preview da conversão */}
            {newPlanCalc && (
                <div className="mt-4 pt-3 border-top" style={{ borderColor: 'rgba(72, 39, 18, 0.08)' }}>
                    <div className="font-size-11 text-muted text-uppercase mb-2 fw-bold" style={{ letterSpacing: "0.5px" }}>
                        Simulação da Troca
                    </div>

                    {newPlanCalc.convertedDays > 0 ? (
                        <>
                            {/* Cards de comparação */}
                            <div className="d-flex align-items-stretch gap-3 mb-3">
                                {/* Plano atual */}
                                <div className="flex-grow-1 p-3 bg-light border text-center" style={{ borderRadius: '12px', borderColor: 'rgba(72, 39, 18, 0.08)' }}>
                                    <div className="text-muted font-size-11 text-uppercase mb-1">Plano Atual</div>
                                    <div className="fw-bold font-size-14 text-dark mb-1">{membership.membershipName}</div>
                                    <div className="font-size-12 text-muted">
                                        {formatCurrency(currentPlanCalc.dailyValue)}/dia
                                    </div>
                                    <div className="font-size-12 text-muted">
                                        Saldo: <strong style={{ color: 'var(--color-brand-accent)' }}>{formatCurrency(currentPlanCalc.remainingValue)}</strong>
                                    </div>
                                </div>

                                {/* Seta */}
                                <div className="d-flex align-items-center px-2 text-muted">
                                    <i className="mdi mdi-arrow-right fs-4"></i>
                                </div>

                                {/* Novo plano */}
                                <div className="flex-grow-1 p-3 bg-white border text-center" style={{ borderRadius: '12px', borderColor: 'var(--color-brand-accent)' }}>
                                    <div className="text-muted font-size-11 text-uppercase mb-1">Novo Plano</div>
                                    <div className="fw-bold font-size-14 text-dark mb-1">{newPlanCalc.plan.name}</div>
                                    <div className="font-size-12 text-muted">
                                        {formatCurrency(newPlanCalc.newDailyValue)}/dia
                                    </div>
                                    <div className="font-size-12">
                                        Vigência: <strong className="text-success">{newPlanCalc.convertedDays} dias</strong>
                                    </div>
                                </div>
                            </div>

                            {/* Resultado */}
                            <div className="d-flex align-items-center bg-white p-3 border mb-3 shadow-sm" style={{ borderRadius: '12px', borderColor: 'rgba(72, 39, 18, 0.08)' }}>
                                <div className="flex-grow-1 text-center">
                                    <div className="text-muted font-size-11 text-uppercase mb-1">Início</div>
                                    <div className="fw-bold font-size-15 text-dark">{formatDate(newPlanCalc.newStartDate)}</div>
                                </div>
                                <div className="px-3 text-muted">
                                    <i className="mdi mdi-arrow-right fs-4"></i>
                                </div>
                                <div className="flex-grow-1 text-center">
                                    <div className="text-muted font-size-11 text-uppercase mb-1">Término</div>
                                    <div className="fw-bold font-size-15 text-dark">{formatDate(newPlanCalc.newEndDate)}</div>
                                </div>
                            </div>

                            {newPlanCalc.convertedDays < currentPlanCalc.daysRemaining && (
                                <Alert color="warning" className="font-size-12 mb-3" style={{ borderRadius: '8px' }}>
                                    <i className="mdi mdi-alert-outline me-1"></i>
                                    O novo plano tem valor diário maior. O aluno terá <strong>{newPlanCalc.convertedDays}</strong> dias ao invés de <strong>{currentPlanCalc.daysRemaining}</strong> (redução de {currentPlanCalc.daysRemaining - newPlanCalc.convertedDays} dias).
                                </Alert>
                            )}

                            {newPlanCalc.convertedDays > currentPlanCalc.daysRemaining && (
                                <Alert color="success" className="font-size-12 mb-3" style={{ borderRadius: '8px' }}>
                                    <i className="mdi mdi-check-circle-outline me-1"></i>
                                    O novo plano tem valor diário menor. O aluno ganhará <strong>{newPlanCalc.convertedDays - currentPlanCalc.daysRemaining}</strong> dias extras de vigência.
                                </Alert>
                            )}

                            <NeoButton
                                variant="primary"
                                onClick={handleConfirm}
                                loading={loading}
                                size="md"
                                className="w-100"
                            >
                                <i className="mdi mdi-swap-horizontal-bold me-2"></i>
                                Confirmar Troca de Plano
                            </NeoButton>
                        </>
                    ) : (
                        <Alert color="danger" className="font-size-13 mb-0" style={{ borderRadius: '8px' }}>
                            <i className="mdi mdi-alert-circle-outline me-1"></i>
                            O saldo restante (<strong>{formatCurrency(currentPlanCalc.remainingValue)}</strong>) não é suficiente para converter em ao menos 1 dia no novo plano ({formatCurrency(newPlanCalc.newDailyValue)}/dia).
                        </Alert>
                    )}
                </div>
            )}

            {catalogPlans.length === 0 && !loadingPlans && (
                <Alert color="info" className="font-size-13 mb-0" style={{ borderRadius: '8px' }}>
                    <i className="mdi mdi-information-outline me-1"></i>
                    Não há outros planos ativos no catálogo para trocar.
                </Alert>
            )}
        </div>
    );
};

export default ActionChangePlan;
